import { motion } from "framer-motion";
import { PRODUCT_META } from "@/data/products";
import { useReveal } from "@/hooks/useReveal";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

function formatNZD(amount) {
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export default function BundleStrip({ product }) {
  const ref = useReveal();
  const { addItem, setIsOpen } = useCart();
  if (!product) return null;
  const meta = PRODUCT_META.butterfly_stocking_bundle;

  const handleAdd = () => {
    addItem(product.id, 1);
    toast.success("Bundle added to your basket — you saved $2!");
    setIsOpen(true);
  };

  return (
    <motion.article
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="reveal mt-8 relative overflow-hidden rounded-[28px] border border-[#EAD6E1]"
      style={{
        background:
          "linear-gradient(115deg, #F6E5EE 0%, #F0D5E4 40%, #F5DDD6 100%)",
      }}
      data-testid={`product-card-${product.id}`}
    >
      {/* Decorative glows */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-20 -left-12 w-72 h-72 rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(#EDC9D8 0%, transparent 60%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-24 -right-12 w-80 h-80 rounded-full opacity-60 blur-3xl"
        style={{ background: "radial-gradient(#F8C8BE 0%, transparent 60%)" }}
      />

      <div className="relative grid md:grid-cols-[1.1fr_1fr] gap-6 md:gap-10 p-6 sm:p-8 md:p-10">
        {/* Left: single editorial hero photo of both products together */}
        <div className="relative min-h-[240px] md:min-h-[320px]">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full rounded-3xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(54,41,42,0.35)] ring-1 ring-white/70"
          >
            <img
              src="/bundle-hero.png"
              alt="Butterfly Collection linen gift bag with Christmas Stocking Stuffer mesh pouch of satin scrunchies, butterfly clip and Me Amor rose-logo tag on a soft blush backdrop"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {/* Save badge */}
            <div className="absolute top-4 left-4 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase text-[#8E4A7B] shadow-sm">
              Save $2
            </div>
          </motion.div>
        </div>

        {/* Right: copy + CTA */}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur border border-white/80 px-3 py-1 text-[10px] tracking-[0.28em] uppercase text-[#8E4A7B]">
              <Sparkles size={12} strokeWidth={2.2} />
              Bundle · Save $2
            </span>
          </div>
          <h3
            className="mt-3 font-serif-me text-3xl sm:text-4xl text-[#36292A] leading-[1.05]"
            data-testid={`bundle-title-${product.id}`}
          >
            Butterfly <span className="italic text-[#8E4A7B]">+</span> Stocking
          </h3>
          <p className="mt-2 font-serif-me italic text-[#8E4A7B] text-lg">
            {meta.subtitle}
          </p>
          <p className="mt-4 text-sm text-[#7D6B6D] leading-relaxed max-w-md">
            {meta.intro}
          </p>

          <div className="mt-6 flex flex-wrap items-baseline gap-3">
            <span
              className="rounded-full px-4 py-1.5 text-sm font-medium tracking-wide text-white"
              style={{ backgroundColor: meta.accent }}
              data-testid={`bundle-price-${product.id}`}
            >
              {formatNZD(product.amount)}
            </span>
            <span className="text-xs text-[#7D6B6D]">
              instead of{" "}
              <span className="line-through">{formatNZD(16)}</span>
            </span>
          </div>

          <button
            type="button"
            onClick={handleAdd}
            className="mt-6 me-btn-primary self-start"
            style={{ backgroundColor: "#8E4A7B" }}
            data-testid={`bundle-add-${product.id}`}
          >
            Add bundle · {formatNZD(product.amount)}
            <span aria-hidden>+</span>
          </button>
          <p className="mt-3 text-[10px] tracking-[0.24em] uppercase text-[#7D6B6D]">
            Both keepsakes shipped together · flat $3 NZ-wide
          </p>
        </div>
      </div>
    </motion.article>
  );
}
